import { useMemo, useState } from "react";
import { Trans, useLingui } from "@lingui/macro";

import type { ScoreDirection, StudyGroup, StudyTrajectory } from "@app/types/study";

import { formatMeasure } from "./format";
import { groupColor } from "@app/lib/palette";

const WIDTH = 760;
const HEIGHT = 300;
// The groups are named by the legend under the chart, never at the lines' ends: the plot keeps the width.
const MARGIN = { top: 28, right: 16, bottom: 40, left: 52 };

/**
 * A score at préop and at each follow-up moment, one line per group: the median of the operated
 * episodes documented there, and their interquartile range.
 *
 * The moments sit at equal steps, as the follow-up calendar is read — on a scale of months, the
 * first six would hold a tenth of the width. Each group is nudged sideways so its ranges do not cover
 * another's. The table under the chart carries every value and, above all, each moment's count: the
 * groups thin out as the follow-up lengthens.
 */
export function TrajectoryChart({
  groups,
  trajectory,
  unit,
  better,
  declaredRange,
}: {
  groups: StudyGroup[];
  trajectory: StudyTrajectory;
  unit: string;
  better: ScoreDirection | null;
  declaredRange: [number, number] | null;
}) {
  const { i18n, t } = useLingui();
  const locale = i18n.locale;
  const [hovered, setHovered] = useState<number | null>(null);
  const moments = trajectory.moments;
  const plotW = WIDTH - MARGIN.left - MARGIN.right;
  const plotH = HEIGHT - MARGIN.top - MARGIN.bottom;
  const step = plotW / Math.max(1, moments.length);
  const dodge = Math.min(10, step / (groups.length + 2));

  const [yMin, yMax] = useMemo(() => {
    if (declaredRange) return declaredRange;
    const values = trajectory.groups.flatMap((cells) =>
      cells.flatMap((cell) => [cell.q1, cell.q3, cell.median]).filter((v): v is number => v !== null),
    );
    if (!values.length) return [0, 1];
    const low = Math.min(...values);
    const high = Math.max(...values);
    const pad = (high - low || 1) * 0.1;
    return [low - pad, high + pad];
  }, [declaredRange, trajectory]);

  const x = (moment: number, group: number) =>
    MARGIN.left + (moment + 0.5) * step + (group - (groups.length - 1) / 2) * dodge;
  const y = (value: number) => MARGIN.top + ((yMax - value) / Math.max(1e-9, yMax - yMin)) * plotH;

  const yTicks = useMemo(() => {
    const span = yMax - yMin;
    const raw = span / 4;
    const power = 10 ** Math.floor(Math.log10(raw || 1));
    const unitStep = [1, 2, 2.5, 5, 10].map((m) => m * power).find((m) => m >= raw) ?? raw;
    const ticks: number[] = [];
    for (let v = Math.ceil(yMin / unitStep) * unitStep; v <= yMax + 1e-9; v += unitStep) ticks.push(v);
    return ticks;
  }, [yMin, yMax]);

  /** Each group's line, broken where a moment is not documented or not shown. */
  const segments = (cells: StudyTrajectory["groups"][number], group: number) => {
    const out: string[] = [];
    let current = "";
    cells.forEach((cell, moment) => {
      if (cell.median === null) {
        if (current) out.push(current);
        current = "";
        return;
      }
      current += `${current ? "L" : "M"}${x(moment, group)},${y(cell.median)}`;
    });
    if (current) out.push(current);
    return out;
  };

  const active = hovered === null ? null : moments[hovered];

  return (
    <div className="space-y-3">
      <div className="relative">
        <svg
          viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
          className="h-auto w-full"
          role="img"
          aria-label={t`Évolution du score par temps de suivi`}
          onPointerLeave={() => setHovered(null)}
        >
          {yTicks.map((tick) => (
            <g key={tick}>
              <line x1={MARGIN.left} x2={WIDTH - MARGIN.right} y1={y(tick)} y2={y(tick)} stroke="#E2E8F0" />
              <text x={MARGIN.left - 8} y={y(tick)} dy="0.32em" textAnchor="end" fontSize="11" fill="#64748B">
                {formatMeasure(tick, locale)}
              </text>
            </g>
          ))}
          <line
            x1={MARGIN.left}
            x2={WIDTH - MARGIN.right}
            y1={HEIGHT - MARGIN.bottom}
            y2={HEIGHT - MARGIN.bottom}
            stroke="#94A3B8"
          />
          {moments.map((moment, index) => (
            <text
              key={moment.key}
              x={MARGIN.left + (index + 0.5) * step}
              y={HEIGHT - MARGIN.bottom + 16}
              textAnchor="middle"
              fontSize="11"
              fill="#64748B"
            >
              {moment.label}
            </text>
          ))}
          {unit && (
            <text x={MARGIN.left - 8} y={MARGIN.top - 16} textAnchor="end" fontSize="11" fill="#64748B">
              {unit}
            </text>
          )}
          {active && hovered !== null && (
            <line
              x1={MARGIN.left + (hovered + 0.5) * step}
              x2={MARGIN.left + (hovered + 0.5) * step}
              y1={MARGIN.top}
              y2={HEIGHT - MARGIN.bottom}
              stroke="#94A3B8"
            />
          )}

          {trajectory.groups.map((cells, group) => (
            <g key={groups[group]?.id ?? group}>
              {cells.map((cell, moment) =>
                cell.q1 !== null && cell.q3 !== null ? (
                  <line
                    key={`r-${moment}`}
                    x1={x(moment, group)}
                    x2={x(moment, group)}
                    y1={y(cell.q1)}
                    y2={y(cell.q3)}
                    stroke={groupColor(group)}
                    strokeWidth={2}
                    strokeLinecap="round"
                    opacity={0.35}
                  />
                ) : null,
              )}
              {segments(cells, group).map((d) => (
                <path
                  key={d}
                  d={d}
                  fill="none"
                  stroke={groupColor(group)}
                  strokeWidth={2}
                  strokeLinejoin="round"
                  strokeLinecap="round"
                />
              ))}
              {cells.map((cell, moment) =>
                cell.median !== null ? (
                  <circle
                    key={`m-${moment}`}
                    cx={x(moment, group)}
                    cy={y(cell.median)}
                    r={4.5}
                    fill={groupColor(group)}
                    stroke="#FFFFFF"
                    strokeWidth={2}
                  />
                ) : null,
              )}
            </g>
          ))}

          {moments.map((moment, index) => (
            <rect
              key={`hit-${moment.key}`}
              x={MARGIN.left + index * step}
              y={MARGIN.top}
              width={step}
              height={plotH}
              fill="transparent"
              tabIndex={0}
              aria-label={moment.label}
              onPointerEnter={() => setHovered(index)}
              onFocus={() => setHovered(index)}
              onBlur={() => setHovered(null)}
            />
          ))}
        </svg>

        {active && hovered !== null && (
          <div
            className="pointer-events-none absolute top-1 z-10 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-2xs text-slate-700 shadow-md"
            style={{ left: `${Math.min(70, ((MARGIN.left + (hovered + 0.5) * step) / WIDTH) * 100)}%` }}
          >
            <div className="mb-1 font-medium text-slate-900">{active.label}</div>
            {trajectory.groups.map((cells, group) => {
              const cell = cells[hovered];
              return (
                <div key={groups[group]?.id ?? group} className="flex items-center gap-1.5 tabular-nums">
                  <span className="h-0.5 w-3 rounded-full" style={{ background: groupColor(group) }} />
                  {cell?.median === null || cell === undefined ? (
                    <span className="text-slate-400">—</span>
                  ) : (
                    <>
                      <strong className="font-semibold text-slate-900">{formatMeasure(cell.median, locale)}</strong>
                      <span className="text-slate-500">
                        [{formatMeasure(cell.q1, locale)} – {formatMeasure(cell.q3, locale)}]
                      </span>
                    </>
                  )}
                  <span className="text-slate-400">
                    <Trans>n = {cell?.n ?? "—"}</Trans>
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {groups.length >= 2 && (
        <ul className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-600">
          {groups.map((group, index) => (
            <li key={group.id} className="flex items-center gap-1.5">
              <span className="h-0.5 w-4 rounded-full" style={{ background: groupColor(index) }} />
              {group.label}
            </li>
          ))}
        </ul>
      )}

      {better && (
        <p className="text-2xs text-slate-500">
          {better === "lower" ? <Trans>Plus bas, c'est mieux.</Trans> : <Trans>Plus haut, c'est mieux.</Trans>}
        </p>
      )}

      <div className="overflow-x-auto">
        <table className="w-full min-w-[640px] text-xs">
          <thead className="text-slate-500">
            <tr>
              <th className="px-3 py-1.5 text-left font-medium">
                <Trans>Groupe</Trans>
              </th>
              {moments.map((moment) => (
                <th key={moment.key} className="px-3 py-1.5 text-right font-medium">
                  {moment.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {trajectory.groups.map((cells, group) => (
              <tr key={groups[group]?.id ?? group} className="border-t border-slate-100">
                <td className="px-3 py-1.5">
                  <span className="flex items-center gap-1.5 text-slate-700">
                    <span className="h-0.5 w-3 rounded-full" style={{ background: groupColor(group) }} />
                    {groups[group]?.label}
                  </span>
                </td>
                {cells.map((cell, moment) => (
                  <td key={moment} className="px-3 py-1.5 text-right tabular-nums text-slate-700">
                    {cell.median === null ? (
                      <span
                        className="text-slate-400"
                        title={cell.n === null ? t`Masqué : moins de personnes que le seuil d'anonymat` : undefined}
                      >
                        —
                      </span>
                    ) : (
                      formatMeasure(cell.median, locale)
                    )}
                    <span className="block text-2xs text-slate-400">
                      <Trans>n = {cell.n ?? "—"}</Trans>
                    </span>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
